/**
 * Chat Modal Component
 * Real-time chat popup with a friend
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFriends } from './friendsContext';
import './ChatModal.css';
import './FriendsModal.css'; // Import for challenge dialog styles
import { API_URL } from '../config/apiConfig';

function ChatModal() {
  const { t } = useTranslation();
  const {
    isChatOpen,
    chatWithUser,
    chatMessages,
    closeChat,
    sendChatMessage,
    sendChallenge
  } = useFriends();

  const [message, setMessage] = useState('');
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [isSendingChallenge, setIsSendingChallenge] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const currentUserId = parseInt(localStorage.getItem('userId'));

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isChatOpen]);

  // Fetch strategies for challenging
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const res = await fetch(`${API_URL}/my-strategies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            // Filter to valid strategies (50-100 orders)
            const valid = data.filter(s => {
              const count = s.orders?.reduce((sum, o) => sum + (o.count || 0), 0) || 0;
              return count >= 50 && count <= 100;
            });
            setStrategies(valid);
            if (valid.length > 0) {
              setSelectedStrategy(valid[0].id.toString());
            }
          }
        }
      } catch (error) {
        console.error('Error fetching strategies:', error);
      }
    };
    
    if (isChatOpen) {
      fetchStrategies();
    }
  }, [isChatOpen]);

  const handleSend = (e) => {
    e.preventDefault();
    
    if (!message.trim() || !chatWithUser) return;

    const success = sendChatMessage(chatWithUser.id, message.trim());
    if (success) {
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleChallengeClick = () => {
    setShowChallengeDialog(true);
  };

  const handleSendChallenge = () => {
    if (!chatWithUser || !selectedStrategy) return;
    
    setIsSendingChallenge(true);
    const success = sendChallenge(chatWithUser.id, parseInt(selectedStrategy));
    if (success) {
      setShowChallengeDialog(false);
    }
    setIsSendingChallenge(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isChatOpen || !chatWithUser) return null;

  return (
    <div className="chat-modal-overlay" onClick={closeChat}>
      <div className="chat-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="chat-header">
          <div className="chat-user-info">
            <span className={`chat-online-indicator ${chatWithUser.online ? 'online' : 'offline'}`} />
            <span className="chat-username">{chatWithUser.username}</span>
          </div>
          <div className="chat-header-actions">
            <button 
              className="friends-action-btn challenge"
              onClick={handleChallengeClick}
              title={t('friends.challengeToFriendlyMatch')}
            >
              ⚔️
            </button>
            <button className="chat-close" onClick={closeChat}>×</button>
          </div>
        </div>

        {/* Challenge Dialog */}
        {showChallengeDialog && (
            <div className="challenge-dialog">
              <div className="challenge-dialog-header">
                {t('friends.challengeTo', { username: chatWithUser.username })}
              </div>
              <div className="challenge-dialog-content">
                <label>{t('friends.selectStrategy')}</label>
              <select 
                value={selectedStrategy} 
                onChange={e => setSelectedStrategy(e.target.value)}
                className="challenge-strategy-select"
              >
                {strategies.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="challenge-dialog-actions">
                <button 
                  className="friends-action-btn cancel"
                  onClick={() => setShowChallengeDialog(false)}
                >
                  {t('friends.cancel')}
                </button>
                <button 
                  className="friends-action-btn challenge"
                  onClick={handleSendChallenge}
                  disabled={!selectedStrategy || strategies.length === 0 || isSendingChallenge}
                >
                  {isSendingChallenge ? t('friends.sending') : t('friends.sendChallenge')}
                </button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="chat-messages">
          {chatMessages.length === 0 ? (
            <div className="chat-empty">
              {t('friends.chat.startConversation', { username: chatWithUser.username })}
            </div>
          ) : (
            chatMessages.map((msg, index) => {
              const isOwn = msg.fromUserId === currentUserId;
              const showTimestamp = index === 0 || 
                chatMessages[index - 1].fromUserId !== msg.fromUserId ||
                msg.timestamp - chatMessages[index - 1].timestamp > 60000;

              return (
                <div 
                  key={msg.id || index} 
                  className={`chat-message ${isOwn ? 'own' : 'other'}`}
                >
                  {showTimestamp && (
                    <div className="chat-timestamp">
                      {formatTime(msg.timestamp)}
                    </div>
                  )}
                  <div className="chat-bubble">
                    {msg.message}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="chat-input-container" onSubmit={handleSend}>
          <input
            ref={inputRef}
            type="text"
            className="chat-input"
            placeholder={t('friends.chat.typeMessage')}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={500}
          />
          <button 
            type="submit" 
            className="chat-send-btn"
            disabled={!message.trim()}
          >
            {t('friends.chat.send')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatModal;

